import { LitElement, type PropertyValues, html } from "lit";

import { customElement, property, query } from "lit/decorators.js";

import "@phosphor-icons/webcomponents/PhCaretLeft";
import { BodyScrollManager, LayoutController } from "ui-common";
import { IOSKeyboardController } from "ui-common/ios-keyboard-controller";
import { SlotContentController } from "ui-common/slot-content-controller";

import { classMap } from "lit/directives/class-map.js";
import { styles } from "./wow-modal.css";

/**
 * A responsive, accessible modal component built with the Popover API.
 *
 * Features responsive design with different behaviors for desktop (overlay) and mobile (fullscreen),
 * smooth animations, and comprehensive accessibility support.
 *
 * @example
 * ```html
 * <wow-modal variant="responsive">
 *   <span slot="header">Modal Title</span>
 *   <p>Modal content goes here</p>
 *   <span slot="footer-button-text">Save Changes</span>
 * </wow-modal>
 * ```
 *
 * @example
 * ```html
 * <!-- Prevent modal from being closed through normal UI interactions -->
 * <wow-modal prevent-close>
 *   <span slot="header">Loading...</span>
 *   <p>Please wait while we process your request...</p>
 * </wow-modal>
 * ```
 *
 * @example
 * ```html
 * <!-- Custom close control with wow-modal-close event -->
 * <wow-modal prevent-close>
 *   <span slot="header">Custom Close</span>
 *   <div>
 *     <p>Content with custom close logic</p>
 *     <button onclick="this.dispatchEvent(new CustomEvent('wow-modal-close', {bubbles: true}))">
 *       Custom Close Button
 *     </button>
 *   </div>
 * </wow-modal>
 * ```
 *
 * @example
 * ```typescript
 * // Handle modal close event
 * modal.addEventListener('close', () => {
 *   modal.remove();
 * });
 * ```
 *
 * @fires close - Emitted when the modal should be closed (after animation completes)
 * @fires close-requested - Emitted when close buttons are clicked but intercept-close is true
 * @fires footer-button-clicked - Emitted when the footer button is clicked
 *
 * @slot header - Content for the modal header/title
 * @slot default - Main content area of the modal
 * @slot footer-button-text - Text content for the primary action button
 *
 * @csspart modal-overlay - The backdrop/overlay element
 * @csspart popover - The main modal container
 * @csspart modal-header - The header section
 * @csspart modal-body - The content area
 * @csspart modal-footer - The footer with action button
 */
@customElement("wow-modal")
export class WowModal extends LitElement {
  static override styles = styles;

  /**
   * Controls the modal display behavior across different screen sizes.
   *
   * - `responsive` (default): Overlay on desktop (≥768px), fullscreen on mobile (<768px)
   * - `overlay`: Always displays as centered overlay regardless of screen size
   * - `fullscreen`: Always displays fullscreen regardless of screen size
   *
   * @example
   * ```html
   * <wow-modal variant="overlay">Centered overlay</wow-modal>
   * <wow-modal variant="fullscreen">Always fullscreen</wow-modal>
   * <wow-modal variant="responsive">Responsive behavior</wow-modal>
   * ```
   */
  @property({ type: String })
  variant: "overlay" | "fullscreen" | "responsive" = "responsive";

  /**
   * When true, prevents the modal from being closed through normal UI interactions.
   * Hides close buttons and footer button, disables ESC key and backdrop clicks.
   * Modal can only be closed programmatically or via 'wow-modal-close' events from slotted content.
   */
  @property({ type: Boolean, attribute: "prevent-close" })
  preventClose = false;

  @property({ type: Boolean, attribute: "hide-primary-button" })
  hidePrimaryButton: boolean = false;

  @property({ type: Boolean, attribute: "disable-primary-button" })
  disablePrimaryButton: boolean = false;

  /**
   * When true, close button clicks emit 'close-requested' event instead of performing close animation automatically.
   * Close buttons remain visible and clickable. Parent component handles the close logic.
   * This is different from 'prevent-close' which hides close buttons entirely.
   */
  @property({ type: Boolean, attribute: "intercept-close" })
  interceptClose = false;

  @property({ type: Boolean, attribute: "alternative-close-button" })
  alternativeCloseButton: boolean = false;

  @property({ type: Boolean, attribute: "alternative-background" })
  alternativeBackground: boolean = false;

  @property({ type: Boolean, attribute: "hide-top-padding" })
  hideTopPadding: boolean = false;

  @property({ type: Boolean, attribute: "mobile-fullscreen" })
  mobileFullscreen = false;

  @property({ type: Boolean, attribute: "mobile-back-arrow" })
  mobileBackArrow = false;

  /**
   * iOS keyboard handling is disabled by default for form modals.
   * Set to true to enable modal shrinking when iOS keyboard opens (useful for chat interfaces).
   */
  @property({ type: Boolean, attribute: "enable-ios-keyboard-handling" })
  enableIosKeyboardHandling = false;

  @query(".modal-overlay")
  private popoverElement!: HTMLElement;

  @query(".popover")
  private popoverSurfaceElement!: HTMLElement;

  private resizeObserver?: ResizeObserver;

  private layoutController = new LayoutController(this);
  private keyboardController = new IOSKeyboardController(this);

  private animationTimeout: number | null = null;
  private mouseDownTarget: EventTarget | null = null;
  private footerSlotController = new SlotContentController(this, "footer-button-text");

  private async handleInterceptClose() {
    if (this.interceptClose) {
      // Emit close-requested for parent to handle
      this.dispatchEvent(
        new CustomEvent("close-requested", {
          bubbles: true,
          composed: true,
        }),
      );
    } else {
      // Normal behavior - perform close animation
      await this.performCloseAnimation();
    }
  }

  private handleCloseClick() {
    this.handleInterceptClose();
  }

  private handleFooterButtonClick() {
    this.dispatchEvent(
      new CustomEvent("footer-button-clicked", {
        composed: true,
      }),
    );
  }

  private handleToggle = (e: Event) => {
    const toggleEvent = e as ToggleEvent;
    if (toggleEvent.newState === "closed" && !this.preventClose) {
      // User closed modal via ESC or outside click
      this.handleInterceptClose();
    }
  };

  private handleContentClick = (e: Event) => {
    // Stop clicks on modal content from bubbling up to the overlay
    e.stopPropagation();
  };

  private handleOverlayMouseDown = (e: MouseEvent) => {
    // Track where the mouse down event started
    this.mouseDownTarget = e.target;
  };

  private handleOverlayMouseUp = (e: MouseEvent) => {
    // Only close if both mouse down and mouse up happened on the overlay (backdrop) and preventClose is false
    if (this.mouseDownTarget === this.popoverElement && e.target === this.popoverElement && !this.preventClose) {
      this.handleInterceptClose();
    }
    // Reset tracking
    this.mouseDownTarget = null;
  };

  private handleOverlayClick = (e: Event) => {
    // Prevent default click behavior since we handle it with mousedown/mouseup
    // This is kept for backwards compatibility but should not close the modal
    e.preventDefault();
  };

  private handleCustomClose = async (e: Event) => {
    // Handle custom close events from slotted content
    e.stopPropagation();
    this.handleInterceptClose();
  };

  /**
   * Sets the modal's closing animation state by manipulating the `data-closing` attribute.
   *
   * This explicit DOM manipulation approach provides clear control over exit animations:
   * - Desktop: Scale from 1.0 to 0.8 with opacity fade
   * - Mobile: Slide right (translateX(100%)) without opacity change
   *
   * @param isClosing - Whether the modal is in closing state
   *
   * @example
   * ```typescript
   * // Start closing animation
   * this.setClosingState(true);
   *
   * // Reset after animation completes
   * setTimeout(() => this.setClosingState(false), 300);
   * ```
   */
  private setClosingState(isClosing: boolean) {
    if (isClosing) {
      this.setAttribute("data-closing", "");
    } else {
      this.removeAttribute("data-closing");
    }
  }

  /**
   * Performs the close animation sequence before dispatching the close event.
   *
   * This method:
   * 1. Sets the closing state to trigger CSS animations
   * 2. Waits for the animation duration (300ms)
   * 3. Resets the closing state
   * 4. Dispatches the 'close' event for parent components to handle
   *
   * The animation behavior varies by screen size:
   * - Desktop: Scale + opacity transition
   * - Mobile: Slide transition (opacity maintained)
   *
   * @returns Promise that resolves when animation completes
   */
  private async performCloseAnimation(): Promise<void> {
    // Trigger the closing animation
    this.setClosingState(true);

    // Wait for the exit animation to complete
    return new Promise<void>((resolve) => {
      // Read animation duration from CSS custom property
      const durationInSeconds =
        parseFloat(getComputedStyle(this).getPropertyValue("--modal-animation-duration-s")) || 0.3; // fallback to 0.3 seconds
      const animationDuration = durationInSeconds * 1000; // Convert to milliseconds

      this.animationTimeout = window.setTimeout(() => {
        this.setClosingState(false);
        this.dispatchCloseEvent();
        resolve();
      }, animationDuration);
    });
  }

  /**
   * Dispatches a 'close' event that parent components should listen for.
   *
   * The parent is responsible for actually removing or hiding the modal element.
   * This separation allows for flexible modal lifecycle management.
   *
   * @example
   * ```typescript
   * modal.addEventListener('close', () => {
   *   // Remove from DOM
   *   modal.remove();
   *
   *   // Or hide with CSS
   *   modal.style.display = 'none';
   *
   *   // Or remove from framework state
   *   this.showModal = false;
   * });
   * ```
   *
   * @fires close - Custom event with no additional data
   */
  private dispatchCloseEvent() {
    this.dispatchEvent(
      new CustomEvent("close", {
        bubbles: true,
        composed: true,
      }),
    );
  }

  override connectedCallback() {
    super.connectedCallback();
    // Listen for custom close events from slotted content
    this.addEventListener("wow-modal-close", this.handleCustomClose);
    // Disable body scrolling when modal is added to DOM
    BodyScrollManager.disableScroll();

    this.manageFullHeight();
  }

  override disconnectedCallback() {
    super.disconnectedCallback();

    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
      this.resizeObserver = undefined;
    }

    // Clean up animation timeout
    if (this.animationTimeout) {
      clearTimeout(this.animationTimeout);
      this.animationTimeout = null;
    }
    // Clean up event listener
    this.removeEventListener("wow-modal-close", this.handleCustomClose);
    // Re-enable body scrolling when modal is removed from DOM
    BodyScrollManager.enableScroll();
  }

  override firstUpdated(props: PropertyValues) {
    super.firstUpdated(props);
    if (this.popoverElement) {
      this.popoverElement.addEventListener("toggle", this.handleToggle);
      // Show the modal when component is first rendered
      this.popoverElement.showPopover();
    }
    this.resizeObserver?.observe(this.popoverSurfaceElement);
  }

  private manageFullHeight() {
    if (this.mobileFullscreen) return;
    if (!this.layoutController.isMobile && !this.popoverSurfaceElement) return;

    this.resizeObserver = new ResizeObserver(() => {
      const viewportHeight = window.innerHeight;
      const contentHeight = this.popoverSurfaceElement.scrollHeight;

      // threshold: 85% of viewport height
      this.mobileFullscreen = contentHeight > viewportHeight * 0.85;
    });
  }

  private renderCloseButton() {
    return html` <button class="close-button desktop-close" @click=${this.handleCloseClick} aria-label="Close modal">
      ${this.alternativeCloseButton ? html`<ph-plus size="24"></ph-plus>` : html`<ph-x-circle size="24"></ph-x-circle>`}
    </button>`;
  }

  private renderMobileBackButton() {
    return html` <button class="back-button mobile-back" @click=${this.handleCloseClick} aria-label="Go back">
      <ph-caret-left size="24"></ph-caret-left>
    </button>`;
  }

  private getPopoverStyle(): string {
    if (
      IOSKeyboardController.isIOS &&
      this.keyboardController.isKeyboardVisible &&
      this.mobileFullscreen &&
      this.enableIosKeyboardHandling
    ) {
      const { viewportHeight, offsetTop } = this.keyboardController;
      return `height: ${viewportHeight}px; transform: translateY(${offsetTop}px)`;
    }
    return "";
  }

  protected override render() {
    const surfaceClasses = {
      popover: true,
      [this.variant]: true,
      fullheight: this.mobileFullscreen,
      "keyboard-visible":
        IOSKeyboardController.isIOS && this.keyboardController.isKeyboardVisible && this.enableIosKeyboardHandling,
    };

    return html`
      <div
        class="modal-overlay"
        popover="manual"
        @mousedown=${this.handleOverlayMouseDown}
        @mouseup=${this.handleOverlayMouseUp}
        @click=${this.handleOverlayClick}
      >
        <div class="${classMap(surfaceClasses)}" style=${this.getPopoverStyle()} @click=${this.handleContentClick}>
          <div class="modal-header">
            ${!this.preventClose && this.mobileBackArrow ? this.renderMobileBackButton() : ""}
            <div class="header-title">
              <slot name="header"></slot>
            </div>
            ${!this.preventClose ? this.renderCloseButton() : ""}
          </div>

          <div class="modal-body">
            <div class="body-columns">
              <slot></slot>
            </div>
            <slot name="one-column"></slot>
          </div>

          ${!this.preventClose && !this.hidePrimaryButton && this.footerSlotController.hasContent
            ? html`
                <div class="modal-footer">
                  <wow-button
                    variant="primary"
                    size="medium"
                    ?disabled=${this.disablePrimaryButton}
                    @click=${this.handleFooterButtonClick}
                  >
                    <slot name="footer-button-text"></slot>
                  </wow-button>
                </div>
              `
            : ""}
        </div>
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    "wow-modal": WowModal;
  }
}
